import { safeParseThemeManifest, ThemeSlug, AssetPath, type ThemeManifest } from "@crashx/theme-schema";
import { applyThemeTokens } from "@crashx/ui";

export const THEMES_BASE_URL = "/themes";

export function themeManifestUrl(slug: string): string {
  return THEMES_BASE_URL + "/" + ThemeSlug.parse(slug) + "/theme.json";
}

/** Absolute URL of an asset referenced by a relative path inside a manifest. */
export function themeAssetUrl(slug: string, relativePath: string, version?: string): string {
  return (
    THEMES_BASE_URL +
    "/" +
    ThemeSlug.parse(slug) +
    "/" +
    AssetPath.parse(relativePath.replace(/^\.\//, "")) +
    (version ? `?version=${encodeURIComponent(version)}` : "")
  );
}

export class ThemeLoadError extends Error {
  constructor(
    readonly slug: string,
    message: string,
  ) {
    super(message);
    this.name = "ThemeLoadError";
  }
}

export interface LoadThemeOptions {
  fetchImpl?: typeof fetch;
  /** Element receiving the CSS variables. Defaults to documentElement. */
  target?: HTMLElement;
  /** Set to false to validate without touching the DOM. */
  apply?: boolean;
}

/**
 * Fetch, validate and apply a theme pack.
 * Throws ThemeLoadError; the caller keeps the fallback tokens from tokens.css,
 * which is a readable greyscale UI rather than a blank screen.
 */
export async function loadTheme(slug: string, options: LoadThemeOptions = {}): Promise<ThemeManifest> {
  const doFetch = options.fetchImpl ?? fetch;
  let response: Response;
  try {
    response = await doFetch(themeManifestUrl(slug), { credentials: "same-origin" });
  } catch (cause) {
    throw new ThemeLoadError(slug, "theme manifest unreachable: " + String(cause));
  }

  if (!response.ok) {
    throw new ThemeLoadError(slug, "theme manifest HTTP " + String(response.status));
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch {
    throw new ThemeLoadError(slug, "theme manifest is not valid JSON");
  }

  const parsed = safeParseThemeManifest(json);
  if (!parsed.success) {
    const detail = parsed.error.issues
      .slice(0, 3)
      .map((i) => i.path.join(".") + ": " + i.message)
      .join("; ");
    throw new ThemeLoadError(slug, "theme manifest rejected by theme-schema — " + detail);
  }

  if (parsed.data.slug !== slug) {
    throw new ThemeLoadError(slug, "manifest slug is " + parsed.data.slug + ", expected " + slug);
  }

  if (options.apply !== false) {
    applyThemeTokens(parsed.data.tokens, options.target);
  }
  return parsed.data;
}
